import { type ReactElement, type ReactNode, useCallback } from 'react'
import { confirmDelete } from '@util'
import {
  CANVASS_MAX_ENTITLEMENTS,
  CANVASS_PRO_ENTITLEMENTS,
  capitalize,
  type Entitlement,
  type EntityId,
  isDefined,
  isNotDefined,
  isOtherEntitlement,
  isPermitEntitlement,
  isResidentEntitlement,
  isTerrosEntitlement,
  OTHER_ENTITLEMENTS,
  PERMIT_ENTITLEMENTS,
  RESIDENT_ENTITLEMENTS,
  TERROS_ENTITLEMENTS,
  type TinyEntitlement,
  isEmpty,
} from '@terros/common'
import { showNotification } from '@mantine/notifications'
import { useDisclosure } from '@mantine/hooks'
import { useForm, type UseFormReturnType } from '@mantine/form'
import { Divider, Group, Modal, MultiSelect, Pill, Select, Stack, Tooltip } from '@mantine/core'
import { useToastError } from '@hooks'
import {
  ENTITLEMENT_SOURCE,
  groupEntitlementsBySource,
  isDirectEntitlement,
  type SelectItem,
  useAddEntitlements,
  useEntitlements,
  useProfile,
  useRemoveEntitlements,
  useTranslation,
} from '@front/model'
import { TAlert, TListItem, TSectionList } from '../terros'
import { ButtonAdd, TButton } from '../buttons'
import { getEntitlementExpiry } from './entitlementExpiry'

type Props = {
  entityId: EntityId
  readOnly?: boolean
}

type FormValues = {
  terrosEntitlements: Entitlement[]
  permitEntitlements: Entitlement[]
  residentEntitlements: Entitlement[]
  otherEntitlements: Entitlement[]
  expiresIn: string | null
}

const EXPIRE_OPTIONS = [
  { value: '', label: 'Never' },
  { value: '7', label: '7 Days' },
  { value: '14', label: '14 Days' },
  { value: '28', label: '28 Days' },
  { value: '90', label: '90 Days' },
  { value: '365', label: '365 Days' },
]

const EXPIRATION_COLOR = 'orange'

export const EditEntitlements = ({ entityId, readOnly = false }: Props): ReactNode => {
  const { isTantalim, isAdmin } = useProfile()
  const canEdit = isTantalim && !readOnly
  const canView = isTantalim || isAdmin
  const { data, error, refresh, isRefreshing } = useEntitlements(entityId, canView)
  const { add, isAdding, error: addError } = useAddEntitlements()
  const { remove, isRemoving, error: removeError } = useRemoveEntitlements()
  const { t } = useTranslation()
  useToastError(addError)
  useToastError(removeError)
  const [isOpen, { toggle }] = useDisclosure()

  const form = useForm<FormValues>({
    initialValues: {
      terrosEntitlements: [],
      permitEntitlements: [],
      residentEntitlements: [],
      otherEntitlements: [],
      expiresIn: '',
    },
  })

  const handleSubmit = async (values: FormValues): Promise<void> => {
    const result = await add({
      entityId,
      entitlements: values.terrosEntitlements.concat(
        values.permitEntitlements,
        values.residentEntitlements,
        values.otherEntitlements
      ),
      expiresAt: getExpiresAt(values.expiresIn),
    })
    form.reset()
    showNotification({
      message: `${result.entitlements.length} entitlements added successfully`,
      color: 'green',
    })
    toggle()
  }

  const toAvailableEntitlements = (entitlements: readonly Entitlement[]): Entitlement[] => {
    const directEntitlements = data?.detailed.filter((e) => isDirectEntitlement(e, entityId)).map((e) => e.entitlement)
    return entitlements.filter((entitlement) => !directEntitlements?.includes(entitlement))
  }

  const handleRemoveEntitlement = async (entitlement: TinyEntitlement): Promise<void> => {
    const confirmed = await confirmDelete({
      title: t('Remove Entitlement'),
      message: t('Are you sure you want to remove {{entitlement}}?', {
        entitlement: normalizeEntitlement(entitlement.entitlement),
      }),
      label: t('Remove'),
      t,
    })
    if (!confirmed) return

    await remove({
      entityId,
      entitlements: [entitlement.entitlement],
    })
    showNotification({
      message: t('Entitlement removed successfully'),
      color: 'green',
    })
  }

  if (!canView) return

  const grouped = groupEntitlementsBySource(data?.detailed ?? [])

  return (
    <>
      <TSectionList
        heading={{
          title: 'Entitlements',
          children: isTantalim ? (
            <Group gap='xs'>
              <TButton variant='ghost' icon='refresh' loading={isRefreshing} onClick={refresh}>
                {t('Refresh')}
              </TButton>
              {canEdit && (
                <ButtonAdd variant='ghost' onClick={toggle}>
                  Add
                </ButtonAdd>
              )}
            </Group>
          ) : undefined,
        }}
      >
        {error && (
          <TAlert title='Error loading entitlements' variant='error'>
            {error}
          </TAlert>
        )}
        {ENTITLEMENT_SOURCE.map((source) => {
          const entitlements = grouped.get(source)
          if (isEmpty(entitlements)) return null
          return (
            <TListItem key={source} label={capitalize(source)} detailBold>
              <Group gap='xs' wrap='wrap'>
                {entitlements.map((entry) => (
                  <EntitlementPill
                    entityId={entityId}
                    key={entry.entitlement}
                    entry={entry}
                    isRemoving={isRemoving}
                    onRemove={handleRemoveEntitlement}
                    canEdit={canEdit}
                  />
                ))}
              </Group>
            </TListItem>
          )
        })}
      </TSectionList>
      <Modal size='lg' title='Add Entitlements' opened={isOpen} onClose={toggle}>
        <form
          onSubmit={(e) => {
            e.stopPropagation()
            form.onSubmit(handleSubmit)(e)
          }}
        >
          <Stack>
            <Group>
              <CanvassPlanPresetButton preset={CANVASS_PRO_ENTITLEMENTS} form={form} label='Canvass Pro Entitlements' />
              <CanvassPlanPresetButton preset={CANVASS_MAX_ENTITLEMENTS} form={form} label='Canvass Max Entitlements' />
            </Group>
            <Divider />

            <MultiSelect
              searchable
              clearable
              {...form.getInputProps('terrosEntitlements')}
              label='Terros Entitlements'
              data={toAvailableEntitlements(TERROS_ENTITLEMENTS).map(toSelectItem)}
            />
            <MultiSelect
              searchable
              clearable
              {...form.getInputProps('permitEntitlements')}
              label='Permit Entitlements'
              data={toAvailableEntitlements(PERMIT_ENTITLEMENTS).map(toSelectItem)}
            />
            <MultiSelect
              searchable
              clearable
              {...form.getInputProps('residentEntitlements')}
              label='Resident Entitlements'
              data={toAvailableEntitlements(RESIDENT_ENTITLEMENTS).map(toSelectItem)}
            />
            <MultiSelect
              searchable
              clearable
              {...form.getInputProps('otherEntitlements')}
              label='Other Entitlements'
              data={toAvailableEntitlements(OTHER_ENTITLEMENTS).map(toSelectItem)}
            />
            <Select {...form.getInputProps('expiresIn')} label='Expires' data={EXPIRE_OPTIONS} clearable />
            <ButtonAdd loading={isAdding} type='submit' />
          </Stack>
        </form>
      </Modal>
    </>
  )
}

type EntitlementPillProps = {
  entityId: EntityId
  entry: TinyEntitlement
  isRemoving: boolean
  onRemove: (entry: TinyEntitlement) => void
  canEdit?: boolean
}

const EntitlementPill = ({
  entityId,
  entry,
  isRemoving,
  onRemove,
  canEdit = false,
}: EntitlementPillProps): ReactElement => {
  const removable = canEdit && isDirectEntitlement(entry, entityId)
  const expiry = getEntitlementExpiry(entry.expiresAt)
  const name = normalizeEntitlement(entry.entitlement)

  const pill = (
    <Pill
      size='md'
      // Focusable so keyboard users can reach the expiry tooltip.
      tabIndex={isDefined(expiry) ? 0 : undefined}
      withRemoveButton={removable}
      onRemove={removable ? () => onRemove(entry) : undefined}
      disabled={isRemoving}
      styles={
        isDefined(expiry)
          ? {
              root: { backgroundColor: `var(--mantine-color-${EXPIRATION_COLOR}-1)` },
              label: { color: `var(--mantine-color-${EXPIRATION_COLOR}-9)` },
            }
          : undefined
      }
    >
      {isDefined(expiry) ? `${name} · ${expiry.label}` : name}
    </Pill>
  )

  if (isNotDefined(expiry)) return pill

  return (
    <Tooltip multiline label={expiry.tooltip} events={{ hover: true, focus: true, touch: false }}>
      {pill}
    </Tooltip>
  )
}

const getExpiresAt = (expiresIn: string | null): number | undefined => {
  if (!expiresIn) return undefined
  const expiresInDays = parseInt(expiresIn)
  const nowSeconds = Math.floor(Date.now() / 1000)
  const ttlSeconds = expiresInDays * 24 * 60 * 60
  return nowSeconds + ttlSeconds
}

const toSelectItem = (entitlement: Entitlement): SelectItem<Entitlement> => ({
  value: entitlement,
  label: normalizeEntitlement(entitlement),
})

const normalizeEntitlement = (entitlement: Entitlement): string => entitlement.split('_').map(capitalize).join(' ')

const CanvassPlanPresetButton = ({
  preset,
  form,
  label,
}: {
  preset: Entitlement[]
  form: UseFormReturnType<FormValues>
  label: string
}): ReactElement => {
  const handleClick = useCallback(() => {
    form.setFieldValue('permitEntitlements', preset.filter(isPermitEntitlement))
    form.setFieldValue('otherEntitlements', preset.filter(isOtherEntitlement))
    form.setFieldValue('terrosEntitlements', preset.filter(isTerrosEntitlement))
    form.setFieldValue('residentEntitlements', preset.filter(isResidentEntitlement))
  }, [form, preset])

  return (
    <TButton variant='gray' onClick={handleClick}>
      {label}
    </TButton>
  )
}
